import { getTowerLevel } from "@/lib/tower";
import { Team, Tournament } from "@/types";
import { useMemo } from "react";

/**
 * Organizes teams into tower levels based on tournament settings
 * Uses getTowerLevel() for consistent level calculations across the app
 *
 * @param teams - Array of teams to organize
 * @param tournament - Tournament configuration
 * @returns Array of arrays, where each inner array represents teams at that level
 */
export function useTeamsByLevel(
  teams: Team[],
  tournament: Tournament | null,
): Team[][] {
  return useMemo(() => {
    if (!tournament || !teams.length) {
      return [];
    }

    // Sort and filter active, placed teams by ranking
    const sortedTeams = [...teams]
      .filter((t) => t.isActive && t.isPlacedInTower !== false)
      .sort((a, b) => a.ranking - b.ranking);

    // Group teams by level based on their position in sorted array
    const levelMap = new Map<number, Team[]>();

    sortedTeams.forEach((team, index) => {
      const position = index + 1; // 1-based position in sorted array

      const level = getTowerLevel(
        position,
        tournament.towerTeamsPerRow,
        tournament.towerTopRowTeamCount,
      );

      if (!levelMap.has(level)) {
        levelMap.set(level, []);
      }
      // Add calculatedPosition for manual inspection without mutating ranking
      levelMap.get(level)!.push({ ...team, calculatedPosition: position });
    });

    // Convert map to sorted array of levels
    const maxLevel = Math.max(...Array.from(levelMap.keys()));
    const levels: Team[][] = [];

    for (let i = 1; i <= maxLevel; i++) {
      levels.push(levelMap.get(i) || []);
    }

    return levels;
  }, [teams, tournament]);
}
